<script setup>
import { computed, ref, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import ContactDetailsItem from '../ContactDetailsItem.vue';
import MultiselectDropdown from 'shared/components/ui/MultiselectDropdown.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import DealStageMover from 'dashboard/components/crm/DealStageMover.vue';
import DealsAPI from 'dashboard/api/crm/deals';

const props = defineProps({
  crmSummary: {
    type: Object,
    default: () => null,
  },
  contactId: {
    type: Number,
    required: true,
  },
});

const { t } = useI18n();
const store = useStore();

// Orden de negocios: abiertos primero, luego el resto (el endpoint ordena por
// posición de stage, no por status).
const STATUS_ORDER = { open: 0, paused: 1, won: 2, lost: 3, canceled: 4 };

const STATUS_STYLES = {
  open: 'text-n-teal-11',
  won: 'text-n-teal-11',
  paused: 'text-n-amber-11',
  lost: 'text-n-ruby-11',
  canceled: 'text-n-ruby-11',
};

const deals = ref([]);
const isFetching = ref(false);
const isRefreshing = ref(false);
const isUpdatingStage = ref(false);
const activeDealIndex = ref(0);
// Cache de stages por pipeline (opciones del dropdown). La mayoría de los deals
// comparten pipeline, así que en la práctica es ~1 fetch por contacto.
const stagesByPipeline = ref({});
const moverRef = ref(null);

const sortedDeals = computed(() =>
  [...deals.value].sort(
    (a, b) => (STATUS_ORDER[a.status] ?? 9) - (STATUS_ORDER[b.status] ?? 9)
  )
);

const hasDeals = computed(() => sortedDeals.value.length > 0);
const hasMultipleDeals = computed(() => sortedDeals.value.length > 1);

const activeDeal = computed(
  () => sortedDeals.value[activeDealIndex.value] || null
);

const dealPositionLabel = computed(
  () => `${activeDealIndex.value + 1}/${sortedDeals.value.length}`
);

const goToPreviousDeal = () => {
  if (activeDealIndex.value > 0) activeDealIndex.value -= 1;
};
const goToNextDeal = () => {
  if (activeDealIndex.value < sortedDeals.value.length - 1) {
    activeDealIndex.value += 1;
  }
};

const formatAmount = (amountCents, currencyCode) =>
  new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: currencyCode || 'ARS',
  }).format((amountCents || 0) / 100);

// Map explícito (no dynamic key) para respetar la convención i18n del repo.
const statusLabels = computed(() => ({
  open: t('CONTACT_PANEL.CRM.STATUS.OPEN'),
  won: t('CONTACT_PANEL.CRM.STATUS.WON'),
  lost: t('CONTACT_PANEL.CRM.STATUS.LOST'),
  canceled: t('CONTACT_PANEL.CRM.STATUS.CANCELED'),
  paused: t('CONTACT_PANEL.CRM.STATUS.PAUSED'),
}));

const statusLabel = status => statusLabels.value[status] || status || '';

// ===== Cabecera contacto-nivel (del crm_summary, sin refetch) =====
const totalDealsCount = computed(() => props.crmSummary?.totalDealsCount ?? 0);

const formattedRevenue = computed(() => {
  if (!props.crmSummary?.totalRevenueByCurrency?.length) {
    return t('CONTACT_PANEL.CRM.NO_REVENUE_DATA');
  }
  return props.crmSummary.totalRevenueByCurrency
    .map(({ currency, amountCents }) => formatAmount(amountCents, currency))
    .join(' + ');
});

const machinesList = computed(() => {
  if (!props.crmSummary?.machinesPurchased?.length) return null;
  return props.crmSummary.machinesPurchased.join(', ');
});

// ===== Detalle del negocio navegado =====
const activeDealPotential = computed(() => {
  const deal = activeDeal.value;
  if (!deal) return null;
  const probability = deal.stage?.probability || 0;
  const cents = Math.round(((deal.amount_cents || 0) * probability) / 100);
  return formatAmount(cents, deal.currency);
});

const stageOptions = computed(() => {
  const deal = activeDeal.value;
  if (!deal) return [];
  const stages = stagesByPipeline.value[deal.pipeline_id] || [];
  return stages.map(stage => ({ id: stage.id, name: stage.name }));
});

const selectedStage = computed(() => {
  const deal = activeDeal.value;
  if (!deal) return null;
  const currentStageId = deal.stage?.id ?? deal.stage_id;
  return stageOptions.value.find(s => s.id === currentStageId) || null;
});

// ===== Fetch de datos =====
const fetchDeals = async () => {
  if (!props.contactId) return;
  isFetching.value = true;
  try {
    const { data } = await DealsAPI.get({ contactId: props.contactId });
    deals.value = data?.deals || [];
  } catch (error) {
    deals.value = [];
  } finally {
    isFetching.value = false;
  }
};

const ensureStagesLoaded = async pipelineId => {
  if (!pipelineId || stagesByPipeline.value[pipelineId]) return;
  try {
    const { data } = await DealsAPI.get({ pipelineId, structureOnly: true });
    stagesByPipeline.value = {
      ...stagesByPipeline.value,
      [pipelineId]: data?.stages || [],
    };
  } catch (error) {
    stagesByPipeline.value = { ...stagesByPipeline.value, [pipelineId]: [] };
  }
};

// Cargar las opciones de etapa del pipeline del deal navegado, on-demand.
watch(
  activeDeal,
  deal => {
    if (deal?.pipeline_id) ensureStagesLoaded(deal.pipeline_id);
  },
  { immediate: true }
);

watch(
  () => props.contactId,
  () => {
    activeDealIndex.value = 0;
    fetchDeals();
  }
);

onMounted(fetchDeals);

// Refresco manual de TODO el acordeón (lista de negocios + totales de la
// cabecera), equivalente a lo que ya dispara un cambio de etapa exitoso — para
// no depender de cerrar/abrir el acordeón para releer datos frescos.
const refreshAll = async () => {
  isRefreshing.value = true;
  try {
    await Promise.all([
      fetchDeals(),
      store.dispatch('contacts/show', {
        id: props.contactId,
        includeCrmSummary: true,
      }),
    ]);
  } finally {
    isRefreshing.value = false;
  }
};

// ===== Cambio de etapa (mismo flujo completo que el board, vía DealStageMover) =====
const onSelectStage = selectedStageOption => {
  const deal = activeDeal.value;
  if (!selectedStageOption || !deal) return;

  isUpdatingStage.value = true;
  moverRef.value?.startMove({
    deal: { id: deal.id, stage_id: deal.stage?.id ?? deal.stage_id },
    targetStageId: selectedStageOption.id,
  });
};

const onDealMoved = async () => {
  const currentDealId = activeDeal.value?.id;
  try {
    await fetchDeals();
    // Mantener el negocio navegado tras el refetch (el orden pudo cambiar).
    if (currentDealId) {
      const idx = sortedDeals.value.findIndex(d => d.id === currentDealId);
      if (idx >= 0) activeDealIndex.value = idx;
    }
    // Refrescar los totales de la cabecera (estado/ingresos pudieron cambiar).
    await store.dispatch('contacts/show', {
      id: props.contactId,
      includeCrmSummary: true,
    });
    useAlert(t('CONTACT_PANEL.CRM.STAGE_UPDATED_SUCCESS'));
  } catch (error) {
    useAlert(t('CONTACT_PANEL.CRM.STAGE_UPDATE_ERROR'));
  } finally {
    isUpdatingStage.value = false;
  }
};

const onDealMoveCancelled = () => {
  isUpdatingStage.value = false;
};

const onDealMoveFailed = () => {
  isUpdatingStage.value = false;
  useAlert(t('CONTACT_PANEL.CRM.STAGE_UPDATE_ERROR'));
};
</script>

<template>
  <div class="flex flex-col">
    <div v-if="isFetching && !hasDeals" class="flex justify-center py-6">
      <Spinner />
    </div>

    <div v-else-if="!hasDeals" class="text-sm text-n-slate-11 p-4">
      {{ t('CONTACT_PANEL.CRM.NO_DATA') }}
    </div>

    <template v-else>
      <!-- Cabecera contacto-nivel -->
      <ContactDetailsItem
        :title="t('CONTACT_PANEL.CRM.TOTAL_DEALS')"
        :value="totalDealsCount"
      >
        <template #button>
          <Button
            icon="i-lucide-refresh-cw"
            variant="ghost"
            color="slate"
            size="xs"
            :title="t('CONTACT_PANEL.CRM.REFRESH')"
            :is-loading="isRefreshing"
            :disabled="isRefreshing"
            @click="refreshAll"
          />
        </template>
      </ContactDetailsItem>
      <ContactDetailsItem
        :title="t('CONTACT_PANEL.CRM.TOTAL_REVENUE')"
        :value="formattedRevenue"
      />
      <ContactDetailsItem
        v-if="machinesList"
        :title="t('CONTACT_PANEL.CRM.MACHINES')"
        :value="machinesList"
      />

      <!-- Tarjeta del negocio navegado (read-only) -->
      <div v-if="activeDeal" class="flex flex-col gap-2 px-4 py-2">
        <div
          class="p-3 rounded-lg bg-n-slate-2 outline outline-1 outline-n-weak"
        >
          <h5
            class="text-sm font-medium text-n-slate-12 truncate"
            :title="activeDeal.title"
          >
            {{ activeDeal.title }}
          </h5>
          <div
            v-if="activeDeal.products?.length"
            class="mt-1 flex flex-wrap gap-1"
          >
            <span
              v-for="product in activeDeal.products"
              :key="product.id"
              class="inline-flex items-center px-2 py-0.5 rounded-full bg-n-alpha-2 text-n-slate-11 text-xs truncate max-w-[140px]"
              :title="product.name"
            >
              {{ product.name }}
            </span>
          </div>
          <p class="mt-1 text-xs text-n-slate-11">
            {{ formatAmount(activeDeal.amount_cents, activeDeal.currency) }}
          </p>
        </div>

        <div
          v-if="hasMultipleDeals"
          class="flex items-center justify-center gap-2"
        >
          <Button
            icon="i-lucide-chevron-left"
            variant="ghost"
            color="slate"
            size="xs"
            :disabled="activeDealIndex === 0"
            @click="goToPreviousDeal"
          />
          <span class="text-xs text-n-slate-11">
            {{ dealPositionLabel }}
          </span>
          <Button
            icon="i-lucide-chevron-right"
            variant="ghost"
            color="slate"
            size="xs"
            :disabled="activeDealIndex === sortedDeals.length - 1"
            @click="goToNextDeal"
          />
        </div>
      </div>

      <!-- Detalle del negocio navegado -->
      <ContactDetailsItem
        v-if="activeDeal"
        :title="t('CONTACT_PANEL.CRM.DEAL_STATUS')"
        :value="statusLabel(activeDeal.status)"
      >
        <span :class="STATUS_STYLES[activeDeal.status]">
          {{ statusLabel(activeDeal.status) }}
        </span>
      </ContactDetailsItem>

      <div
        v-if="activeDeal && stageOptions.length"
        class="multiselect-wrap--small px-4"
      >
        <ContactDetailsItem
          compact
          :title="t('CONTACT_PANEL.CRM.ACTIVE_STAGE')"
        />
        <div :class="{ 'opacity-50 pointer-events-none': isUpdatingStage }">
          <MultiselectDropdown
            :options="stageOptions"
            :selected-item="selectedStage"
            :has-thumbnail="false"
            :multiselector-title="t('CONTACT_PANEL.CRM.ACTIVE_STAGE')"
            :multiselector-placeholder="t('CONTACT_PANEL.CRM.SELECT_STAGE')"
            :no-search-result="t('CONTACT_PANEL.CRM.NO_STAGES_FOUND')"
            :input-placeholder="t('CONTACT_PANEL.CRM.SEARCH_STAGE')"
            @select="onSelectStage"
          />
        </div>
      </div>

      <ContactDetailsItem
        v-if="activeDeal"
        :title="t('CONTACT_PANEL.CRM.POTENTIAL_REVENUE')"
        :value="activeDealPotential"
      />

      <DealStageMover
        ref="moverRef"
        @moved="onDealMoved"
        @cancelled="onDealMoveCancelled"
        @move-failed="onDealMoveFailed"
      />
    </template>
  </div>
</template>
